import { MultiPolygon } from 'geojson';
import { DomEvent, LatLng, LeafletMouseEvent, Point } from 'leaflet';
import { observer } from 'mobx-react-lite';
import { Fragment, ReactElement } from 'react';
import { Polygon, PolygonProps } from 'react-leaflet';
import { DEFAULT_STROKE_OPACITY } from '../../../helpers/global.constants';
import { ISegmentation, ISegmentationFeature } from '../annotations.interface';
import { AnnotationToolType } from '../submodules/annotationTypes/models/annotationToolType';
import { PointFeature } from './features/PointFeature';
import { PolygonFeature } from './features/PolygonFeature';
import { PolylineFeature } from './features/PolylineFeature';
import { RectangleFeature } from './features/RectangleFeature';
import { RotatedRectangleFeature } from './features/RotatedRectangleFeature';
import { VectorFeature } from './features/VectorFeature';

export interface ISegmentationPolygonProps {
  color: string;
  storeKey: string;
  type: AnnotationToolType;
  canRotate: boolean;
}

interface ISegmentationLayerFeaturesProps {
  segmentations: ISegmentation[];
  selectedSegmentation?: ISegmentation;
  hoveredSegmentation?: string;
  readonly?: boolean;
  featureInEditId?: string;
  opacityLevel: number;
  zoomLevel: number;
  activeAnnotationTypeId?: string;
  contextMenuSegmentationId?: string;
  reactLeafletRenderFingerprint: string;
  onSegmentationSelected(id: string): void;
  onSegmentationOver(segmentation: string): void;
  onSegmentationOut(): void;
  onContextMenu(position: Point, segmentationId: string): void;
}

export const SegmentationLayerFeatures = observer((props: ISegmentationLayerFeaturesProps): ReactElement => {
  const handleSegmentationOver =
    (segmentationId: string) =>
    (_: LeafletMouseEvent): void => {
      if (props.featureInEditId === undefined) {
        props.onSegmentationOver(segmentationId);
      }
    };

  const handleSegmentationOut =
    (segmentationId: string) =>
    (_: LeafletMouseEvent): void => {
      if (props.featureInEditId === undefined && props.hoveredSegmentation === segmentationId) {
        props.onSegmentationOut();
      }
    };

  const handleClick =
    (feature: ISegmentationFeature) =>
    (e: LeafletMouseEvent): void => {
      DomEvent.stop(e.originalEvent);
      if (props.activeAnnotationTypeId !== undefined && props.activeAnnotationTypeId !== AnnotationToolType.SELECT) {
        return;
      }
      props.onSegmentationSelected(feature.id);
    };

  const handleContextMenu =
    (feature: ISegmentationFeature) =>
    (e: LeafletMouseEvent): void => {
      if (props.activeAnnotationTypeId !== undefined && props.activeAnnotationTypeId !== AnnotationToolType.SELECT) {
        return;
      }

      if (!props.readonly && (!props.featureInEditId || props.featureInEditId === feature.id)) {
        props.onContextMenu(e.containerPoint, feature.id);
        props.onSegmentationSelected(feature.id);
      }
    };

  const composeKey = (segmentation: ISegmentation): string => {
    return `${segmentation.feature.id} ${props.reactLeafletRenderFingerprint} ${props.readonly}`;
  };

  const renderFeature = (segmentation: ISegmentation): ReactElement => {
    const key = composeKey(segmentation);

    const { feature, latlngs } = segmentation;
    const selectedOpacity = props.opacityLevel / 100;

    const isEditedSegmentation =
      (props.featureInEditId !== undefined && feature.id !== props.featureInEditId) ||
      (props.contextMenuSegmentationId !== undefined && feature.id !== props.contextMenuSegmentationId);

    const opacity = isEditedSegmentation ? DEFAULT_STROKE_OPACITY : 1;
    const fillOpacity = isEditedSegmentation ? selectedOpacity / 2 : selectedOpacity;
    const isActive = props.selectedSegmentation?.feature.id === segmentation.feature.id && !props.readonly && !props.activeAnnotationTypeId;

    switch (feature.featureType) {
      case AnnotationToolType.POINT: {
        const point = (latlngs as LatLng[])[0];
        return (
          <PointFeature
            isActive={isActive}
            center={point}
            radius={4}
            key={key}
            storeKey={feature.id}
            fillOpacity={fillOpacity}
            color={feature.color}
            type={AnnotationToolType.POINT}
            onclick={handleClick(feature)}
            oncontextmenu={handleContextMenu(feature)}
            onmouseover={handleSegmentationOver(feature.id)}
            onmouseout={handleSegmentationOut(feature.id)}
            canRotate={false}
            opacity={opacity}
            className='with-context-menu'
          />
        );
      }
      case AnnotationToolType.RECTANGLE:
        return (
          <RectangleFeature
            isActive={isActive}
            positions={latlngs}
            key={key}
            storeKey={feature.id}
            fillOpacity={fillOpacity}
            color={feature.color}
            type={AnnotationToolType.ROTATEDRECTANGLE}
            onclick={handleClick(feature)}
            oncontextmenu={handleContextMenu(feature)}
            onmouseover={handleSegmentationOver(feature.id)}
            onmouseout={handleSegmentationOut(feature.id)}
            canRotate={false}
            opacity={opacity}
            className='with-context-menu'
          />
        );
      case AnnotationToolType.POLYGON:
        return (
          <PolygonFeature
            isActive={isActive}
            key={key}
            storeKey={feature.id}
            fillOpacity={fillOpacity}
            color={feature.color}
            positions={latlngs}
            type={AnnotationToolType.POLYGON}
            onclick={handleClick(feature)}
            oncontextmenu={handleContextMenu(feature)}
            onmouseover={handleSegmentationOver(feature.id)}
            onmouseout={handleSegmentationOut(feature.id)}
            canRotate={false}
            opacity={opacity}
            className='with-context-menu'
          />
        );
      case AnnotationToolType.ROTATEDRECTANGLE:
        return (
          <RotatedRectangleFeature
            isActive={isActive}
            key={key}
            feature={feature}
            latlngs={latlngs as LatLng[]}
            opacity={opacity}
            fillOpacity={fillOpacity}
            onSegmentationOver={handleSegmentationOver(feature.id)}
            onSegmentationOut={handleSegmentationOut(feature.id)}
            onClick={handleClick(feature)}
            onContextMenu={handleContextMenu(feature)}
            className='with-context-menu'
            zoomLevel={props.zoomLevel}
          />
        );
      case AnnotationToolType.POLYLINE:
        return (
          <PolylineFeature
            isActive={isActive}
            key={key}
            storeKey={feature.id}
            fillOpacity={fillOpacity}
            color={feature.color}
            type={AnnotationToolType.POLYLINE}
            positions={latlngs as LatLng[][]}
            onclick={handleClick(feature)}
            oncontextmenu={handleContextMenu(feature)}
            onmouseover={handleSegmentationOver(feature.id)}
            onmouseout={handleSegmentationOut(feature.id)}
            canRotate={false}
            opacity={opacity}
            className='with-context-menu'
          />
        );
      case AnnotationToolType.VECTOR: {
        return (
          <VectorFeature
            key={key}
            isActive={isActive}
            feature={feature}
            fillOpacity={fillOpacity}
            latlngs={latlngs as LatLng[]}
            onClick={handleClick(feature)}
            onContextMenu={handleContextMenu(feature)}
            onSegmentationOut={handleSegmentationOut(feature.id)}
            onSegmentationOver={handleSegmentationOver(feature.id)}
            opacity={opacity}
            zoomLevel={props.zoomLevel}
          />
        );
      }
      case AnnotationToolType.BRUSH: {
        const geometry = feature.geometry as MultiPolygon;
        if (
          props.selectedSegmentation?.feature.id === feature.id &&
          geometry.coordinates.length &&
          !props.readonly &&
          props.activeAnnotationTypeId === props.selectedSegmentation.feature.properties?.annotationTypeId
        ) {
          return <></>;
        }

        return (
          <Polygon<ISegmentationPolygonProps & PolygonProps>
            key={key}
            storeKey={feature.id}
            fillOpacity={fillOpacity}
            color={feature.color}
            positions={latlngs}
            type={AnnotationToolType.BRUSH}
            onclick={handleClick(feature)}
            oncontextmenu={handleContextMenu(feature)}
            onmouseover={handleSegmentationOver(feature.id)}
            onmouseout={handleSegmentationOut(feature.id)}
            canRotate={false}
            opacity={opacity}
            className='with-context-menu'
          />
        );
      }
      case AnnotationToolType.SELECT:
      default:
        return <></>;
    }
  };

  return (
    <>
      {props.segmentations.map((segmentation: ISegmentation) => (
        <Fragment key={segmentation.feature.id}>{renderFeature(segmentation)}</Fragment>
      ))}
    </>
  );
});
